import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { handleHttpError } from '../../helpers/handleHttpError';
import { FirmwarePackageDTO } from '../../infrastructure/backends/firmware-package.dto';
import {
  FirmwareUploadResponseDto,
  PackageUpdateRequestBody,
  PackageUpdateResponseDto
} from '../../infrastructure/backends/firmware-upload-package.dto';
import { ReleaseNotesDTO } from '../../infrastructure/backends/release-notes.dto';
import { ApiRemoteUpdate } from '../../infrastructure/services/api-remote-update';
import { FirmwareDataStoreType } from '../../infrastructure/types/firmware-api.types';
import {
  BackendFirmwarePackageDetailsDto,
  PackageChangeHistoryDto
} from '../../infrastructure/types/firmware-upload.types';

const initialState: FirmwareDataStoreType = {
  uploadPackage: { data: null, loading: false, error: null },
  updatePackage: { data: null, loading: false, error: null },
  packageVersion: { data: null, loading: false, error: null },
  allPackages: { data: null, loading: false, error: null },
  allLangauges: { data: null, loading: false, error: null },
  firmwarePackageDetails: { data: null, loading: false, error: null },
  packageChangeHistory: { data: null, loading: false, error: null },
  releaseFirmwarePackage: { data: null, loading: false, error: null },
  deletePackage: { data: null, loading: false, error: null },
  withdrawPackage: { data: null, loading: false, error: null },
  allSupportedApps: { data: null, loading: false, error: null },
  submit: { data: null, loading: false, error: null }
};

export const useFirmwareStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(store => {
    const api = inject(ApiRemoteUpdate);
    return {
      loadPackageVersion: async (firmwareVersionStatus: string) => {
        patchState(store, state => ({
          packageVersion: { ...state.packageVersion, loading: true }
        }));
        try {
          const data = await firstValueFrom(
            api.get<{ availabilityStatus: string }>('/packages/version-availability', {
              version: firmwareVersionStatus
            })
          );
          patchState(store, state => ({
            packageVersion: { ...state.packageVersion, data, loading: false, error: null }
          }));
        } catch (error) {
          patchState(store, state => ({
            packageVersion: {
              ...state.packageVersion,
              loading: false,
              error: handleHttpError(error, 'Failed to load package status')
            }
          }));
        }
      },
      loadAllLanguages: async () => {
        patchState(store, state => ({
          allLangauges: { ...state.allLangauges, loading: false }
        }));
        try {
          const languages = await firstValueFrom(api.get<ReleaseNotesDTO[]>('/supportedLanguages'));
          const languageArray = Array.isArray(languages) ? languages : [];
          patchState(store, state => ({
            allLangauges: {
              ...state.allLangauges,
              data: languageArray,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            allLangauges: {
              ...state.allLangauges,
              loading: false,
              error: handleHttpError(error, 'Failed to load supported languages')
            }
          }));
        }
      },
      loadAllPackages: async () => {
        patchState(store, state => ({
          allPackages: { ...state.allPackages, loading: true }
        }));

        try {
          const data = await firstValueFrom(api.get<FirmwarePackageDTO[]>('/packages'));
          patchState(store, state => ({
            allPackages: { ...state.allPackages, data, loading: false, error: null }
          }));
        } catch (error) {
          patchState(store, state => ({
            allPackages: {
              ...state.allPackages,
              loading: false,
              error: handleHttpError(error, 'Failed to load all packages')
            }
          }));
        }
      },
      loadAllPackageDetails: async (packageId: number) => {
        patchState(store, state => ({
          firmwarePackageDetails: { ...state.firmwarePackageDetails, loading: true }
        }));
        try {
          const data = await firstValueFrom(
            api.get<BackendFirmwarePackageDetailsDto>(`/v1/package/${packageId}`)
          );
          patchState(store, state => ({
            firmwarePackageDetails: {
              ...state.firmwarePackageDetails,
              data,
              loading: false,
              error: null
            }
          }));
          return data;
        } catch (error) {
          patchState(store, state => ({
            firmwarePackageDetails: {
              ...state.firmwarePackageDetails,
              loading: false,
              error: handleHttpError(error, 'Failed to load all details of the firmware package')
            }
          }));
          throw error;
        }
      },
      uploadFirmwarePackage: async (file: File, metadata: FirmwareUploadResponseDto) => {
        patchState(store, state => ({
          uploadPackage: { ...state.uploadPackage, loading: true }
        }));

        try {
          await firstValueFrom(api.postFile('/v1/package', file, metadata));
          patchState(store, state => ({
            uploadPackage: { ...state.uploadPackage, loading: false, error: null }
          }));
        } catch (error) {
          patchState(store, state => ({
            uploadPackage: {
              ...state.uploadPackage,
              loading: false,
              error: handleHttpError(error, 'Upload failed')
            }
          }));
          throw error;
        }
      },
      loadAllPackageChangeHistory: async (packageId: number) => {
        patchState(store, state => ({
          packageChangeHistory: { ...state.packageChangeHistory, loading: true }
        }));
        try {
          const data = await firstValueFrom(
            api.get<PackageChangeHistoryDto[]>(`/package/${packageId}/history`)
          );
          patchState(store, state => ({
            packageChangeHistory: {
              ...state.packageChangeHistory,
              data,
              loading: false,
              error: null
            }
          }));
          return data;
        } catch (error) {
          patchState(store, state => ({
            packageChangeHistory: {
              ...state.packageChangeHistory,
              loading: false,
              error: handleHttpError(error, 'Failed to load all the package change history')
            }
          }));
          throw error;
        }
      },
      releasePackage: async (packageId: number) => {
        patchState(store, state => ({
          releaseFirmwarePackage: { ...state.releaseFirmwarePackage, loading: true }
        }));
        try {
          await firstValueFrom(api.post(`/package/${packageId}/release`));

          patchState(store, state => ({
            releaseFirmwarePackage: {
              ...state.releaseFirmwarePackage,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            releaseFirmwarePackage: {
              ...state.releaseFirmwarePackage,
              loading: false,
              error: handleHttpError(error, 'Failed to release package')
            }
          }));
          throw error;
        }
      },
      deleteFirmwarePackage: async (packageId: number) => {
        patchState(store, state => ({
          deletePackage: { ...state.deletePackage, loading: true }
        }));
        try {
          await firstValueFrom(api.delete(`/package/${packageId}`));

          patchState(store, state => ({
            deletePackage: { ...state.deletePackage, loading: false, error: null }
          }));
        } catch (error) {
          patchState(store, state => ({
            deletePackage: {
              ...state.deletePackage,
              loading: false,
              error: handleHttpError(error, 'Failed to delete package')
            }
          }));
          throw error;
        }
      },
      withdrawFirmwarePackage: async (packageId: number) => {
        patchState(store, state => ({
          withdrawPackage: { ...state.withdrawPackage, loading: true }
        }));
        try {
          await firstValueFrom(api.post(`/package/${packageId}/withdraw`));

          patchState(store, state => ({
            withdrawPackage: {
              ...state.withdrawPackage,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            withdrawPackage: {
              ...state.withdrawPackage,
              loading: false,
              error: handleHttpError(error, 'Failed to withdraw package')
            }
          }));
          throw error;
        }
      },
      updateFirmwarePackage: async (packageId: number, metadata: PackageUpdateRequestBody) => {
        patchState(store, state => ({
          updatePackage: { ...state.updatePackage, loading: true }
        }));
        try {
          const updatedFirmwarePackage = await firstValueFrom(
            api.put<PackageUpdateResponseDto, PackageUpdateRequestBody>(
              `/v1/package/${packageId}`,
              metadata
            )
          );
          patchState(store, state => ({
            updatePackage: {
              ...state.updatePackage,
              data: updatedFirmwarePackage,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            updatePackage: {
              ...state.updatePackage,
              loading: false,
              error: handleHttpError(error, 'Failed to update package')
            }
          }));
          throw error;
        }
      },
      loadAllSupportedApps: async () => {
        patchState(store, state => ({
          allSupportedApps: { ...state.allSupportedApps, loading: true }
        }));
        try {
          const data = await firstValueFrom(
            api.get<{ name: string; appleMinimumVersion: string; androidMinimumVersion: string }[]>(
              '/v1/supportedApps'
            )
          );
          patchState(store, state => ({
            allSupportedApps: { ...state.allSupportedApps, data, loading: false, error: null }
          }));
        } catch (error) {
          patchState(store, state => ({
            allSupportedApps: {
              ...state.allSupportedApps,
              loading: false,
              error: handleHttpError(error, 'Failed to load supported apps')
            }
          }));
          throw error;
        }
      }
    };
  })
);
